import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { useAuth } from '../contexts/AuthContext';
import { useAuthRemount } from '../App';
import { BACKEND_URL } from '../config';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

import {
  CheckCircle,
  CircleNotch,
  Eye,
  EyeSlash,
  SignIn
} from '@phosphor-icons/react';
const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const forceAuthRemount = useAuthRemount();
  // Removed isProcessing - GlobalAuthGate handles the loading screen
  const [error, setError] = useState('');
  const [emailFormData, setEmailFormData] = useState({ email: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Track if we're in the middle of OAuth processing to avoid rendering login form
  const [oauthInProgress, setOauthInProgress] = useState(false);

  useEffect(() => {
    // Check for google_auth=success parameter (custom Google OAuth callback)
    const googleAuthSuccess = searchParams.get('google_auth');
    const redirectPath = searchParams.get('redirect');
    
    if (googleAuthSuccess === 'success') {
      setOauthInProgress(true); // Show loading state instead of login form
      processGoogleAuthSuccess(redirectPath);
      return;
    }
    
    // Check for session_id in URL fragment (Emergent OAuth callback - legacy)
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      setOauthInProgress(true); // Prevent login form from rendering during OAuth
      processSessionId(hash);
    }
    // Note: We don't need checkExistingSession() anymore
    // AuthRouteGuard handles redirecting authenticated users before this component renders
  }, [searchParams]);

  const processGoogleAuthSuccess = async (redirectPath) => {
    // Custom Google OAuth was successful - session cookie is already set
    // Just need to refresh auth state and redirect
    setError('');

    try {
      console.log('[LOGIN DEBUG] Google OAuth successful, refreshing auth state');
      
      // Clear any stale cached auth data FIRST
      const keysToRemove = [
        'user', 'role', 'isAdmin', 'activeOrg', 'navMode',
        'impersonated_user_name', 'impersonated_user_email'
      ];
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      // Determine final redirect path - check for pending intents first
      const postAuthRedirect = localStorage.getItem('post_auth_redirect');
      const pendingBlueprint = localStorage.getItem('pending_blueprint_claim');
      const installIntent = localStorage.getItem('install_intent');
      let finalRedirect;
      
      if (pendingBlueprint && postAuthRedirect) {
        finalRedirect = postAuthRedirect; // /blueprint/continue
      } else if (installIntent && postAuthRedirect) {
        finalRedirect = postAuthRedirect; // /marketplace/{slug}?auto_install=true
      } else if (redirectPath) {
        finalRedirect = redirectPath;
      } else {
        // Determine redirect based on user plan/role
        try {
          const meRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
          if (meRes.ok) {
            const meData = await meRes.json();
            const userRole = meData.role || 'member';
            const isAdmin = userRole === 'admin' || userRole === 'primary_admin';
            const userPlan = (meData.plan || 'starter').toLowerCase();
            const isFreeTier = ['starter', 'basic', 'free'].includes(userPlan);
            
            if (isFreeTier) {
              finalRedirect = '/dashboard';
            } else if (isAdmin) {
              finalRedirect = '/dashboard';
            } else {
              finalRedirect = '/dashboard';
            }
          } else {
            finalRedirect = '/dashboard';
          }
        } catch (e) {
          finalRedirect = '/dashboard';
        }
      }
      
      // SOLUTION: Instead of trying to coordinate forceAuthRemount + refreshUser + navigate,
      // just do a hard redirect. The page reload will trigger a fresh auth check
      // with the session cookie that was just set by the backend.
      // This is more reliable than trying to synchronize React state updates.
      console.log('[LOGIN DEBUG] Google OAuth complete, redirecting to:', finalRedirect);
      
      // Clean URL and redirect in one step
      window.location.href = finalRedirect;

    } catch (error) {
      console.error('Google auth processing error:', error);
      setError('Authentication failed. Please try again.');
      setOauthInProgress(false);
      window.history.replaceState(null, '', '/login');
    }
  };

  const processSessionId = async (hash) => {
    // Don't show separate loading screen - GlobalAuthGate will handle it after forceAuthRemount
    setError('');

    try {
      // Extract session_id from URL fragment
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      if (!sessionIdMatch) {
        throw new Error('Invalid session ID');
      }

      const sessionId = sessionIdMatch[1];

      // Call backend to process session
      const response = await fetch(`${BACKEND_URL}/api/auth/process-session`, {
        method: 'POST',
        headers: {
          'X-Session-ID': sessionId
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      // Clean URL fragment
      window.history.replaceState(null, '', window.location.pathname);

      // Clear any stale cached auth data
      const keysToRemove = [
        'user', 'role', 'isAdmin', 'activeOrg', 'navMode',
        'impersonated_user_name', 'impersonated_user_email'
      ];
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      console.log('[LOGIN DEBUG] Google OAuth successful, forcing AuthProvider remount');
      
      // CRITICAL: Force AuthProvider remount to destroy any stale state
      forceAuthRemount();
      
      // Wait for auth context to refresh with new user data
      await refreshUser();
      
      // Fetch user role for proper redirect
      const userResponse = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      // Check for redirect query param first
      const redirectParam = searchParams.get('redirect');
      let redirectPath = redirectParam || '/partner-pro/workspace';  // Use param or default for members
      
      // If no redirect param, determine based on role
      if (!redirectParam && userResponse.ok) {
        const userData = await userResponse.json();
        const userRole = userData.role || 'member';
        const isAdmin = userRole === 'admin' || userRole === 'primary_admin';
        
        console.log('[LOGIN DEBUG] Got user data for redirect', {
          userId: userData.id,
          userRole,
          email: userData.email
        });
        
        redirectPath = isAdmin ? '/dashboard' : '/partner-pro/workspace';
      }

      console.log('[LOGIN DEBUG] Navigating to:', redirectPath);
      
      // Use navigate() instead of window.location.href to avoid page reload flash
      navigate(redirectPath, { replace: true });

    } catch (error) {
      console.error('Authentication error:', error);
      setError('Authentication failed. Please try again.');
      setOauthInProgress(false); // Allow login form to render again on error
      
      // Clean URL fragment on error
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  const handleGoogleLogin = () => {
    // Use centralized BACKEND_URL config which handles production detection
    const redirectParam = searchParams.get('redirect');
    const googleAuthUrl = `${BACKEND_URL}/api/auth/google/login${redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : ''}`;
    window.location.href = googleAuthUrl;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          email: emailFormData.email,
          password: emailFormData.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      console.log('[LOGIN DEBUG] Email login successful, forcing AuthProvider remount');
      
      // CRITICAL: Force AuthProvider remount to destroy any stale state
      forceAuthRemount();
      
      // Wait for auth context to refresh
      await refreshUser();

      // Fetch user info to determine role-based redirect
      const userResponse = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      // Check for redirect query param first
      const redirectParam = searchParams.get('redirect');
      
      // Check for pending intent redirects
      const postAuthRedirect = localStorage.getItem('post_auth_redirect');
      const pendingBlueprint = localStorage.getItem('pending_blueprint_claim');
      const installIntent = localStorage.getItem('install_intent');
      
      let redirectPath;
      
      if (pendingBlueprint && postAuthRedirect) {
        // Blueprint user - go to claim flow
        redirectPath = postAuthRedirect;
      } else if (installIntent && postAuthRedirect) {
        // Install intent from marketplace - go to worker install
        redirectPath = postAuthRedirect;
      } else if (redirectParam) {
        redirectPath = redirectParam;
      } else {
        // Default: Use param or default for members
        redirectPath = '/partner-pro/workspace';
        
        // If no redirect param, determine based on role and plan
        if (userResponse.ok) {
          const userData = await userResponse.json();
          const userRole = userData.role || 'member';
          const isAdmin = userRole === 'admin' || userRole === 'primary_admin';
          
          // Check if onboarding should be reset
          if (userData.reset_onboarding) {
            localStorage.removeItem('mm_onboarding_done');
          }
          
          // All tiers → unified dashboard for admins, workspace for members
          redirectPath = isAdmin ? '/dashboard' : '/partner-pro/workspace';
        }
      }

      // Use navigate() instead of window.location.href
      navigate(redirectPath, { replace: true });

    } catch (error) {
      setError(error.message || 'Login failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmailFormData({ ...emailFormData, email: e.target.value });
    setError('');
  };

  const handlePasswordChange = (e) => {
    setEmailFormData({ ...emailFormData, password: e.target.value });
    setError('');
  };

  // During OAuth callback processing, show placeholder with matching background
  // This prevents blank frame - GlobalAuthGate will take over after forceAuthRemount
  if (oauthInProgress) {
    // Return a nice loading state that matches the app theme
    return (
      <div 
        className="fixed inset-0 bg-[#161B26] flex items-center justify-center" 
        style={{ minHeight: '100vh', minWidth: '100vw' }}
      >
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-[rgba(0,229,192,0.1)] flex items-center justify-center mx-auto mb-6">
            <CircleNotch size={32} className="text-[#00E5C0] animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Signing you in...</h2>
          <p className="text-[#A0AEC0] text-sm">Please wait while we set up your session</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161B26]">
      <SEO 
        title="Login - Man & Machine AI Opportunity Analyzer"
        description="Sign in to access your AI Opportunity Analyzer and unlock business automation insights."
      />
      <LandingHeader />

      <div className="pt-32 pb-20">
        <div className="max-w-md mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-3xl p-8"
          >
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[rgba(99,102,241,0.1)] flex items-center justify-center mx-auto mb-4">
                <SignIn size={32} className="text-[#00E5C0]" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Welcome Back
              </h1>
              <p className="text-[#A0AEC0]">
                Sign in to continue to your AI Opportunity Analyzer
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/30"
              >
                <p className="text-[#EF4444] text-sm">{error}</p>
              </motion.div>
            )}

            {/* Email/Password SignIn Form */}
            <form onSubmit={handleEmailLogin} className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={emailFormData.email}
                  onChange={handleEmailChange}
                  className="w-full px-4 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0] transition-all"
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={emailFormData.password}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0] transition-all"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#718096] hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-full bg-[#00E5C0] hover:bg-[#00CCB0] disabled:opacity-50 transition-colors"
              >
                <span className="flex items-center justify-center gap-2 py-4 text-[#0B0F14] font-bold text-lg">
                  {isSubmitting ? (
                    <>
                      <CircleNotch size={20} className="animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </span>
              </button>
            </form>

            {/* Forgot Password Link */}
            <div className="text-center mb-6">
              <Link to="/forgot-password" className="text-sm text-[#00E5C0] hover:text-[#00CCB0] transition-colors">
                Forgot your password?
              </Link>
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[rgba(255,255,255,0.1)]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#161B26] text-[#718096]">Or continue with</span>
              </div>
            </div>

            {/* Google Sign In Button */}
            <button
              onClick={handleGoogleLogin}
              type="button"
              className="w-full relative overflow-hidden rounded-full group mb-6 border border-[rgba(255,255,255,0.1)] hover:border-[#00E5C0] transition-colors"
            >
              <div className="absolute inset-0 bg-[rgba(0,229,192,0.1)] opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <span className="relative z-10 flex items-center justify-center gap-3 py-4 text-white font-semibold text-base">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </span>
            </button>

            {/* Features */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-[#A0AEC0] text-sm">
                <CheckCircle size={20} className="text-[#14F195] flex-shrink-0" />
                <span>No password required</span>
              </div>
              <div className="flex items-center gap-3 text-[#A0AEC0] text-sm">
                <CheckCircle size={20} className="text-[#14F195] flex-shrink-0" />
                <span>Instant access to AI Opportunity Analysis</span>
              </div>
              <div className="flex items-center gap-3 text-[#A0AEC0] text-sm">
                <CheckCircle size={20} className="text-[#14F195] flex-shrink-0" />
                <span>Secure & encrypted</span>
              </div>
            </div>

            {/* Terms */}
            <p className="text-xs text-center text-[#718096] mt-8">
              By continuing, you agree to our{' '}
              <Link to="/terms" className="text-[#00E5C0] hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-[#00E5C0] hover:underline">Privacy Policy</Link>
            </p>

            {/* Register Link */}
            <div className="text-center mt-6">
              <p className="text-[#A0AEC0] text-sm">
                Don&apos;t have an account?{' '}
                <Link to="/register" className="text-[#00E5C0] hover:text-[#00CCB0] font-semibold transition-colors">
                  Sign up
                </Link>
              </p>
            </div>
          </motion.div>

          {/* Back to Home */}
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/')}
              className="text-[#A0AEC0] hover:text-white transition-colors text-sm"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Login;
